// src/pages/Dashboard/Payouts/Payouts.tsx
import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";

import { fetchPayoutMethods } from "../../redux/payoutMethodSlice";
import { listenToHostPayout } from "../../redux/payoutsSlice";

import {
  Wallet,
  Banknote,
  Mail,
  Globe,
  Building2,
  BarChart3,
  History,
  Eye,
  EyeOff,
} from "lucide-react";

import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";

// ================== HELPERS ==================
const maskLast4 = (value?: string) => {
  if (!value) return "—";
  const clean = value.replace(/\s+/g, "");
  return "**** " + clean.slice(-4);
};

const mapName = (method: string) =>
  (method ?? "unknown")
    .replace(/_/g, " ")
    .toUpperCase();

// ================== LOCAL TYPES ==================
interface Address {
  country: string;
  city: string;
  firstLine: string;
  postCode: string;
}

interface BasePayoutMethod {
  fullName: string;
  legalType: string;
  currency: string;
  createdAt?: string;
  updatedAt?: string;
}

interface SkrillPayout extends BasePayoutMethod {
  payoutMethod: "skrill";
  skrillEmail: string;
}

interface WiseEurIbanPayout extends BasePayoutMethod {
  payoutMethod: "wise_eur_iban";
  iban: string;
  bic?: string;
  address: Address;
}

interface PaypalPayout extends BasePayoutMethod {
  payoutMethod: "paypal";
  paypalEmail: string;
}

interface WiseUsdSwiftPayout extends BasePayoutMethod {
  payoutMethod: "wise_usd_swift";
  swiftCode: string;
  accountNumber: string;
  address: Address;
}
interface PayoneerPayout extends BasePayoutMethod {
  payoutMethod: "payoneer";
  payoneerEmail: string;
}

interface WireTransferPayout extends BasePayoutMethod {
  payoutMethod: "wire";
  beneficiaryFirst: string;
  beneficiaryLast: string;
  iban?: string;
  swift: string;
  bankName: string;
  bankAddress: string;
  accountNumber: string;
}

type PayoutMethod =
  | SkrillPayout
  | WiseEurIbanPayout
  | PaypalPayout
  | WiseUsdSwiftPayout
  | PayoneerPayout       // if added earlier
  | WireTransferPayout;  // ⭐ NEW

// =====================================================
//                        COMPONENT
// =====================================================
const Payouts: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { payoutMethods, loading } = useSelector(
    (state: RootState) => state.payoutMethod
  );

  const { pendingBalance } = useSelector((state: RootState) => state.payouts);
  const user = useSelector((state: RootState) => state.auth.user);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);

  const userId = user?.userId || user?.uid;

  useEffect(() => {
    if (userId) {
      dispatch(fetchPayoutMethods({ userId }));
      dispatch(listenToHostPayout(userId));
    }
  }, [userId, dispatch]);

  const toggleExpand = (key: string) =>
    setExpanded(expanded === key ? null : key);

  const goToBank = () => navigate(`/PayoutsMethods/${userId}`);
  const goToAnalytics = () => navigate(`/MerchantAnalytics/${userId}`);
  const goToHistory = () => navigate(`/TransactionsHistory/${userId}`);

  const noMethods = !Object.keys(payoutMethods || {}).length;

  // MASKED VALUE
  const maskedValue = useMemo(() => {
    const formatted = (pendingBalance ?? 0)
      .toLocaleString("en-US", { minimumFractionDigits: 2 })
      .replace(/[^0-9]/g, "");
    return "*".repeat(formatted.length);
  }, [pendingBalance]);

  const renderIcon = (method: any) => {
    const type = method?.payoutMethod || "";

    if (type.includes("wise")) return <Globe size={20} />;
    if (type === "skrill") return <Mail size={20} />;
    if (type === "paypal") return <Banknote size={20} />;
if (type === "payoneer") return <Mail size={20} />; // same style as Skrill
if (type === "wire") return <Building2 size={20} />;

    return <Wallet size={20} />;
  };

  if (loading) {
    return (
      <div style={styles.loaderContainer}>
        <CircularProgress sx={{ color: "#bf9b30" }} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* ================= BLUE REVOLUT CARD WITH EYE ================= */}
      <div style={styles.balanceCard}>
        <div style={styles.topRow}>
          <h4 style={styles.balanceLabel}>Total Wealth</h4>

          <div style={styles.eyeBox} onClick={() => setHidden(!hidden)}>
            {hidden ? <EyeOff size={24} color="#fff" /> : <Eye size={24} color="#fff" />}
          </div>
        </div>

        <p style={styles.bigBalance}>
          €
          {hidden
            ? maskedValue
            : (pendingBalance ?? 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
        </p>

        <div style={styles.iconRow}>
          <div style={styles.iconBtn} onClick={goToBank}>
            <Building2 size={26} color="#bf9b30" />
            <span style={styles.iconLabel}>Bank Details</span>
          </div>

          <div style={styles.iconBtn} onClick={goToAnalytics}>
            <BarChart3 size={26} color="#bf9b30" />
            <span style={styles.iconLabel}>Analytics</span>
          </div>

          <div style={styles.iconBtn} onClick={goToHistory}>
            <History size={26} color="#bf9b30" />
            <span style={styles.iconLabel}>Transactions</span>
          </div>
        </div>
      </div>

      {/* ================= LIST OF METHODS ================= */}
      <h2 style={styles.sectionTitle}>Payout Methods</h2>

      {noMethods ? (
        <div style={styles.emptyStateBox} onClick={goToBank}>
          <Wallet size={40} color="#bf9b30" />
          <p style={{ marginTop: 10, color: "#ddd" }}>No payout methods added yet.</p>
          <button style={styles.addBtn}>Add Payout Method</button>
        </div>
      ) : (
        <div style={styles.cardGrid}>
          {Object.entries(payoutMethods as Record<string, PayoutMethod>).map(
            ([key, method]) => (
              <motion.div
                key={key}
                layout
                onClick={() => toggleExpand(key)}
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 0 25px rgba(191,155,48,0.35)",
                }}
                style={{
                  ...styles.card,
                  borderColor:
                    expanded === key ? "#bf9b30" : "rgba(191,155,48,0.2)",
                }}
              >
                <div style={styles.cardHeader}>
                  <div style={styles.iconBox}>{renderIcon(method)}</div>

                  <div>
                    <h3 style={styles.methodTitle}>
                      {mapName(method?.payoutMethod)}
                    </h3>

                    <p style={styles.methodSub}>
                      {method.currency} · {method.legalType}
                    </p>
                  </div>
                </div>

                <AnimatePresence>
                  {expanded === key && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      style={styles.detailsBox}
                    >
                      <DetailRow label="Full Name" value={method.fullName} />


                      {"skrillEmail" in method && (
                        <DetailRow
                          label="Skrill Email"
                          value={method.skrillEmail}
                        />
                      )}
{"payoneerEmail" in method && (
  <DetailRow label="Payoneer Email" value={method.payoneerEmail} />
)}

                      {"paypalEmail" in method && (
                        <DetailRow
                          label="PayPal Email"
                          value={method.paypalEmail}
                        />
                      )}

                      {"address" in method && (
                        <>
                          <DetailRow
                            label="Country"
                            value={method.address.country}
                          />
                          <DetailRow
                            label="City"
                            value={method.address.city}
                          />
                          <DetailRow
                            label="Street"
                            value={method.address.firstLine}
                          />
                          <DetailRow
                            label="Postal Code"
                            value={method.address.postCode}
                          />
                        </>
                      )}
{"beneficiaryFirst" in method && (
  <DetailRow label="Beneficiary First Name" value={method.beneficiaryFirst} />
)}

{"beneficiaryLast" in method && (
  <DetailRow label="Beneficiary Last Name" value={method.beneficiaryLast} />
)}

{"iban" in method && (
  <DetailRow label="IBAN" value={maskLast4(method.iban ?? "")} />
)}

{"swift" in method && (
  <DetailRow label="SWIFT" value={method.swift} />
)}

{"bankName" in method && (
  <DetailRow label="Bank Name" value={method.bankName} />
)}

{"bankAddress" in method && (
  <DetailRow label="Bank Address" value={method.bankAddress} />
)}

{"accountNumber" in method && (
  <DetailRow
    label="Account Number"
    value={maskLast4(method.accountNumber)}
  />
)}

                      <div style={styles.timeRow}>
                        <span>
                          Added:{" "}
                          {method.createdAt
                            ? new Date(method.createdAt).toLocaleDateString()
                            : "—"}
                        </span>
                        <span>
                          Updated:{" "}
                          {method.updatedAt
                            ? new Date(method.updatedAt).toLocaleDateString()
                            : "—"}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default Payouts;

// =====================================================
//                      DETAIL ROW
// =====================================================
const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: string | number | undefined;
}) => (
  <div style={styles.detailRow}>
    <span style={styles.detailLabel}>{label}</span>
    <span style={styles.detailValue}>{value || "—"}</span>
  </div>
);

// =====================================================
//                        STYLES
// =====================================================
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    color: "#fff",
    padding: "20px 24px 80px",
    fontFamily: "'Poppins', sans-serif",
  },
  balanceCard: {
    background: "linear-gradient(180deg, #111b3f 0%, #070a12 100%)",
    textAlign: "center",
    padding: "35px 20px",
    borderRadius: 26,
    marginBottom: 35,
    boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  balanceLabel: {
    fontSize: 15,
    opacity: 0.9,
    color: "#fff",
    fontWeight: 600,
  },
  eyeBox: {
    padding: 6,
    borderRadius: 12,
    cursor: "pointer",
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.3)",
    backdropFilter: "blur(6px)",
  },
  bigBalance: {
    marginTop: 10,
    fontSize: 50,
    fontWeight: 900,
    background: "linear-gradient(90deg,#ffffff,#bf9b30)",
    WebkitBackgroundClip: "text",
    color: "transparent",
  },
  iconRow: {
    marginTop: 28,
    display: "flex",
    justifyContent: "center",
    gap: 18,
  },
  iconBtn: {
    background: "rgba(255,255,255,0.05)",
    padding: "12px 18px",
    borderRadius: 14,
    border: "1px solid rgba(191,155,48,0.35)",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: 110,
  },
  iconLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#bf9b30",
    marginTop: 6,
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 10,
    fontWeight: 600,
    color: "#bf9b30",
  },
  emptyStateBox: {
    marginTop: 40,
    background: "linear-gradient(145deg,#0a0a0a,#111)",
    borderRadius: 16,
    padding: 30,
    textAlign: "center",
    border: "1px solid rgba(191,155,48,0.3)",
    cursor: "pointer",
  },
  addBtn: {
    marginTop: 12,
    padding: "10px 14px",
    borderRadius: 10,
    background: "#bf9b30",
    color: "#000",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 20,
    marginTop: 10,
  },
  card: {
    background: "linear-gradient(145deg, #0a0a0a, #111)",
    borderRadius: 16,
    padding: 20,
    border: "1px solid rgba(191,155,48,0.2)",
    boxShadow: "0 3px 10px rgba(0,0,0,0.4)",
    transition: "all 0.3s ease",
    cursor: "pointer",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  iconBox: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    background: "linear-gradient(145deg, #111, #000)",
    border: "1px solid rgba(191,155,48,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#bf9b30",
  },
  methodTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#fff",
  },
  methodSub: {
    fontSize: 13,
    color: "#bbb",
  },
  detailsBox: {
    marginTop: 15,
    borderTop: "1px solid rgba(191,155,48,0.25)",
    paddingTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 13.5,
    color: "#ddd",
    borderBottom: "1px dashed rgba(191,155,48,0.2)",
    paddingBottom: 4,
  },
  detailLabel: { color: "#bf9b30", fontWeight: 500 },
  detailValue: { color: "#fff" },
  timeRow: {
    marginTop: 8,
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    color: "#999",
  },
  loaderContainer: {
    height: "60vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};
